import { Link } from "react-router-dom";
import { Gift, Sparkles } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import type { PackDto } from "@gaole/shared";
import { useAuth } from "../stores/auth";
import { api } from "../lib/api";
import { PackCoverflow } from "../components/home/PackCoverflow";
import { LivePullMarquee } from "../components/home/LivePullMarquee";
import { EmptyState, PageLoader } from "../components/ui";

export function Packs() {
  const { user, moduleEnabled } = useAuth();
  const collectionOn = moduleEnabled("collection");

  const { data, isLoading } = useQuery({
    queryKey: ["packs"],
    queryFn: () => api<{ items: PackDto[] }>("/packs"),
  });

  const { data: myPulls } = useQuery({
    queryKey: ["my-pulls-count"],
    queryFn: () => api<{ items: { revealed: boolean }[] }>("/packs/mine/pulls").catch(() => ({ items: [] })),
    enabled: Boolean(user) && collectionOn,
  });
  const unrevealed = myPulls?.items.filter((p) => !p.revealed).length ?? 0;

  if (isLoading) return <PageLoader />;

  const packs = data?.items ?? [];

  return (
    <div className="space-y-6 px-4 pb-10 pt-4 lg:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-extrabold lg:text-2xl">Mystery packs</h1>
          <p className="mt-1 max-w-2xl text-xs text-white/50">
            Pick your pack, tear the foil, flip each card. Every pool shows its full contents and live odds.
          </p>
        </div>
        {collectionOn && unrevealed > 0 && (
          <Link
            to="/inventory"
            className="pulse-ring inline-flex shrink-0 items-center gap-1.5 rounded-full bg-gradient-brand px-3.5 py-2 text-[11px] font-bold"
          >
            <Sparkles size={12} />
            {unrevealed} card{unrevealed > 1 ? "s" : ""} to flip
          </Link>
        )}
      </div>

      <LivePullMarquee />

      {packs.length === 0 ? (
        <EmptyState icon={Gift} title="No packs available right now" hint="Check back soon — new pools drop often." />
      ) : (
        <PackCoverflow packs={packs} />
      )}
    </div>
  );
}
